import { Button, Form, Input, Spin, Typography, Radio, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { PhoneNumberUtil } from "google-libphonenumber";
import { ContactFormRole } from "@/enums/contact-form-role.enum";
import { getInTouchForm } from "@/api/core";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import CountryCodeSelect from "@/components/Common/CountryCodeSelect";
import PhoneInput from "@/components/Common/PhoneInput";
import Image from "next/image";
import Right_Tick from "@/public/images/Right_tick.webp";

const SignUpNotifyFormModal = ({
  signUpNotify,
  handleSignUpToNotify,
  title = "Sign Up to Notify",
  buttonText = "Sign Up to Notify",
  formType = "join",
  examInfo,
}) => {
  const [contactForm] = Form.useForm();
  const countryCode = Form.useWatch("countryCode", contactForm);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const phoneUtil = PhoneNumberUtil.getInstance();
  const roleOptions = [
    { label: "Parent", value: ContactFormRole.PARENT },
    { label: "Educator", value: ContactFormRole.Educator },
    { label: "Partner", value: ContactFormRole.Partner },
  ];

  useEffect(() => {
    contactForm.setFieldValue("countryCode", "+1");
  }, []);

  const handleSignUpToNotifyForm = (values: any) => {
    const { countryCode, phone, ...rest } = values;
    setContactSubmitting(true);
    const payload = phone
      ? {
          ...rest,
          phone: countryCode + phone,
          exam: examInfo?.exam,
          examLevel: examInfo?.examLevel,
        }
      : {
          ...rest,
          exam: examInfo?.exam,
          examLevel: examInfo?.examLevel,
        };
    getInTouchForm(payload).then((res) => {
      setContactSubmitting(false);
      setShowSuccessMessage(true);
    });
  };

  const onSelectCountryCode = (val: any) => {
    contactForm.setFieldValue("countryCode", val);
    contactForm.validateFields(["countryCode"]);
    const phoneValue = contactForm.getFieldValue("phone");
    if (phoneValue) {
      contactForm.validateFields(["phone"]);
    }
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
    contactForm.resetFields();
    handleSignUpToNotify();
  };

  const handleCloseModal = () => {
    contactForm.resetFields();
    handleSignUpToNotify();
  };

  const validatePhoneNumber = (_, value) => {
    if (!value) return Promise.resolve();

    if (!countryCode) {
      return Promise.resolve();
    }

    try {
      const phoneNumberWithCode = `${countryCode}${value}`;
      const number = phoneUtil.parseAndKeepRawInput(phoneNumberWithCode, "");
      return phoneUtil.isValidNumber(number)
        ? Promise.resolve()
        : Promise.reject("Enter valid phone number.");
    } catch {
      return Promise.reject("Enter valid phone number.");
    }
  };

  return (
    <>
      {signUpNotify && (
        <Modal
          title={null}
          open={signUpNotify}
          footer={null}
          centered
          onCancel={handleCloseModal}
        >
          <div>
            {!showSuccessMessage ? (
              <div className="w-full">
                <Typography.Title
                  level={5}
                  style={{
                    color: "#2D68C4",
                    textAlign: "center",
                    fontSize: "32px",
                  }}
                >
                  {title}
                </Typography.Title>
                <Form
                  layout="vertical"
                  className="w-full"
                  form={contactForm}
                  onFinish={handleSignUpToNotifyForm}
                  scrollToFirstError
                  name="Sign Up to Notify"
                  disabled={contactSubmitting}
                >
                  <Form.Item
                    label={"Name"}
                    name="name"
                    rules={[
                      { required: true, message: "Please enter your name" },
                      {
                        min: 2,
                        message: "Name must be at least 2 characters long",
                      },
                      {
                        max: 100,
                        message: "Name cannot exceed 100 characters",
                      },
                      {
                        pattern: /^[A-Za-z\s]+$/,
                        message: "Name can only contain letters and spaces",
                      },
                    ]}
                    validateTrigger={["onBlur", "onChange"]}
                  >
                    <Input
                      prefix={<UserOutlined className="text-gray-400 mr-1" />}
                      placeholder="Enter your name"
                      status={
                        contactForm.getFieldError("name").length > 0
                          ? "error"
                          : ""
                      }
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label="You Are"
                    name="role"
                    rules={[
                      { required: true, message: "please enter your role" },
                    ]}
                    validateTrigger={["onBlur", "onChange"]}
                  >
                    <Radio.Group
                      name="contact-info-radio-group"
                      options={roleOptions}
                    />
                  </Form.Item>

                  <Form.Item
                    label={"Email"}
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email" },
                      {
                        type: "email",
                        message: "Please enter a valid email address",
                      },
                      { max: 254, message: "Email address is too long" },
                    ]}
                    validateTrigger={["onBlur", "onChange"]}
                  >
                    <Input
                      prefix={<MailOutlined className="text-gray-400 mr-1" />}
                      placeholder="Enter your email"
                      status={
                        contactForm.getFieldError("email").length > 0
                          ? "error"
                          : ""
                      }
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span>
                        Phone No. <span className="opacity-40">(optional)</span>
                      </span>
                    }
                    className="mb-0 w-full"
                  >
                    <div className="flex items-center gap-2">
                      <Form.Item name="countryCode" className="min-w-36">
                        <CountryCodeSelect
                          onSelectCountryCode={onSelectCountryCode}
                          field={{ value: countryCode }}
                          isDisabled={true}
                        />
                      </Form.Item>
                      <Form.Item
                        name="phone"
                        rules={[
                          {
                            validator: validatePhoneNumber,
                          },
                        ]}
                        className="w-full"
                        validateTrigger={["onBlur", "onChange"]}
                      >
                        <PhoneInput
                          placeholder="Enter your phone number"
                          countryCode={countryCode}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>

                  <Form.Item className="mb-0">
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="h-11 w-full bg-[#2D68C4]"
                      loading={
                        contactSubmitting
                          ? {
                              icon: (
                                <Spin size="small" className="spin-white" />
                              ),
                            }
                          : false
                      }
                      disabled={contactSubmitting}
                    >
                      {buttonText}
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2">
                <Image
                  src={Right_Tick}
                  alt="Right_Tick_Contact_Us"
                  width={100}
                  height={100}
                />
                <Typography.Title
                  level={3}
                  className="text-center !text-[#2D68C4]"
                >
                  Welcome to StedPrep
                </Typography.Title>
                {formType === "notify" ? (
                  <>
                    <Typography.Paragraph className="text-center">
                      {`You're now part of our early access community. You'll be among the first to know when we launch and start sharing exciting updates.`}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="text-center">
                      {`We'll be in touch with early access details - stay tuned!`}
                    </Typography.Paragraph>
                  </>
                ) : (
                  <>
                    <Typography.Paragraph className="text-center">
                      {`Thank you for signing up! You're now on the list to be among the first to explore StedPrep when we launch.`}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="text-center">
                      {`We'll be in touch soon with updates and early access details - stay tuned!`}
                    </Typography.Paragraph>
                  </>
                )}
                <Button
                  type="primary"
                  htmlType="button"
                  className="bg-[#2D68C4]"
                  onClick={handleCloseSuccessMessage}
                >
                  Got it
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default SignUpNotifyFormModal;
